import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Avatar } from "@/components/common/Avatar";
import { relative, HEALTH_DOT } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Building2, Plus } from "lucide-react";
import { toast } from "sonner";

const AMC_STATUSES = ["active", "expiring", "expired", "none"];
const HEALTH_STATUSES = ["green", "amber", "red"];

export default function Clients() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: clients, isLoading } = useQuery({ queryKey: ["clients"], queryFn: () => get("/crm/clients") });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = Object.fromEntries(users.map((u) => [u.id, u]));
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [amcFilter, setAmcFilter] = useState("all");
  const [healthFilter, setHealthFilter] = useState("all");

  const create = useMutation({
    mutationFn: (body) => post("/crm/clients", body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["clients"] }); toast.success("Client created"); },
  });

  const filtered = useMemo(() => {
    if (!clients) return [];
    return clients.filter((c) => {
      if (search && !c.name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (ownerFilter !== "all" && c.account_owner_id !== ownerFilter) return false;
      if (amcFilter !== "all" && (c.amc?.status || "none") !== amcFilter) return false;
      if (healthFilter !== "all" && c.health !== healthFilter) return false;
      return true;
    });
  }, [clients, search, ownerFilter, amcFilter, healthFilter]);

  return (
    <div>
      <PageHeader title="Clients" subtitle="Active accounts converted from won deals.">
        <FormDialog title="New Client" testid="new-client-modal"
          trigger={<Button data-testid="new-client-btn"><Plus className="mr-1.5 h-4 w-4" />New Client</Button>}
          fields={[
            { name: "name", label: "Client Name", required: true, full: true },
            { name: "account_owner_id", label: "Account Owner", type: "select", required: true, options: users.map((u) => ({ value: u.id, label: u.name })) },
            { name: "industry", label: "Industry", placeholder: "e.g. retail" },
          ]}
          onSubmit={(v) => create.mutateAsync(v)} />
      </PageHeader>
      {isLoading ? <Skeleton className="h-64" /> : clients?.length === 0 ? (
        <EmptyState icon={Building2} title="No clients yet" subtitle="Clients appear here once a lead is won and converted." />
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <SearchInput testid="client-search" placeholder="Search clients" value={search} onChange={setSearch} />
            <Select value={ownerFilter} onValueChange={setOwnerFilter}>
              <SelectTrigger className="w-44" data-testid="client-owner-filter"><SelectValue placeholder="All owners" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All owners</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={amcFilter} onValueChange={setAmcFilter}>
              <SelectTrigger className="w-44" data-testid="client-amc-filter"><SelectValue placeholder="All AMC statuses" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All AMC statuses</SelectItem>
                {AMC_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={healthFilter} onValueChange={setHealthFilter}>
              <SelectTrigger className="w-40" data-testid="client-health-filter"><SelectValue placeholder="All health" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All health</SelectItem>
                {HEALTH_STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <EmptyState icon={Building2} title="No clients match" subtitle="Adjust your search or filters." />
          ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="w-8 px-4 py-3"></th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Account Owner</th>
                <th className="px-4 py-3 font-semibold">Active Projects</th>
                <th className="px-4 py-3 font-semibold">AMC Status</th>
                <th className="px-4 py-3 font-semibold">Converted</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} onClick={() => navigate(`/crm/clients/${c.id}`)}
                  className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3">
                    <span data-testid={`client-health-${c.id}`} title={c.health} className={cn("block h-2.5 w-2.5 rounded-full", HEALTH_DOT[c.health] || "bg-slate-400")} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 font-medium text-foreground">
                      <Building2 className="h-4 w-4 text-muted-foreground" />{c.name}
                    </div>
                  </td>
                  <td className="px-4 py-3"><div className="flex items-center gap-1.5"><Avatar user={umap[c.account_owner_id]} size="xs" /><span className="text-xs">{umap[c.account_owner_id]?.name}</span></div></td>
                  <td className="px-4 py-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{c.active_projects}</span></td>
                  <td className="px-4 py-3"><StatusBadge status={c.amc?.status} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{relative(c.converted_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
          )}
        </>
      )}
    </div>
  );
}
